import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import API_BASE_URL from "../../src/config";
import Sidebar from "./Sidebar";
import { ClipLoader } from "react-spinners";
import { formatLocalDate } from "../utils/dateUtils";


const BookingCard = ({ booking }) => {
  const {
    stay,
    room,
    summary: {
      check_in,
      check_out,
      guest_count,
      nights,
      room_count,
      total_price,
      booking_code,
    } = {},
  } = booking;

  return (
    <div className="trip-card" key={booking.id}>
      <div className="trip-card-body">
        {/* Room Image */}
        {room?.images?.[0]?.url && (
          <img
            src={room.images[0].url}
            alt={room.title}
            style={{
              width: "100%",
              maxHeight: "200px",
              objectFit: "cover",
              borderRadius: "8px",
              marginBottom: "10px",
            }}
          />
        )}

        {/* Stay Info */}
        <h5>{stay?.location?.location_name}</h5>
        <div className="date">
          <span>
            {check_in ? new Date(check_in).toLocaleDateString("en-GB", { timeZone: "UTC" }) : "--"} -{" "}
            {check_out ? new Date(check_out).toLocaleDateString("en-GB", { timeZone: "UTC" }) : "--"}
          </span>
          <span>{guest_count || 0} Guests</span>
        </div>

        <p>
          <strong>Stay:</strong> {stay?.title || "--"}
        </p>
        <p>
          <strong>Address:</strong> {stay?.address || "--"}
        </p>

        {/* Room Info */}
        <p>
          <strong>Room:</strong> {room?.title || "--"}
        </p>
        <p>
          <strong>Price/Night:</strong> ₹
          {room?.price_per_night
            ? parseFloat(room.price_per_night).toLocaleString()
            : 0}
          <br />
          <strong>Tax/Night:</strong> ₹
          {room?.tax_per_night
            ? parseFloat(room.tax_per_night).toLocaleString()
            : 0}
        </p>

        {/* Summary */}
        <p>
          <strong>Nights:</strong> {nights || 0}
        </p>
        <p>
          <strong>Room Count:</strong> {room_count || 0}
        </p>
        <p>
          <strong>Total Price:</strong> ₹
          {total_price ? parseFloat(total_price).toLocaleString() : 0}
        </p>
      </div>

      {/* Footer with Details Button */}
      <div className="trip-card-footer">
        {booking_code && (
          <Link to={`/booking-details/${booking_code}`} className="theme-btn">
            View Details
          </Link>
        )}
      </div>
    </div>
  );
};

//. Main Component
const PropertyBookings = () => {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // 🔑 Token Handling
  const storedUser = JSON.parse(localStorage.getItem("user") || "{}");
  const token = storedUser.accessToken || localStorage.getItem("accessToken");

  const handleUpdateStatus = async (bookingId, newStatus) => {
    try {
      await axios.post(
        `${API_BASE_URL}bookings/owner/update-status`,
        { booking_id: bookingId, action: newStatus },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      // Optional optimistic UI update
      setBookings((prev) =>
        prev.map((b) =>
          b.id === bookingId
            ? { ...b, summary: { ...b.summary, status: newStatus } }
            : b
        )
      );

      //. REFRESH LIST FROM API
      fetchBookings();
    } catch (error) {
      console.error("Status update failed:", error);
      alert(error.response?.data?.message || "Action failed");
    }
  };


  const fetchBookings = async () => {
    try {
      const response = await axios.post(
        `${API_BASE_URL}bookings/bookinglist-byowner`,
        {},
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.data.success) {
        if (response.data.status === "fetched") {
          setBookings(response.data.data?.bookings || []);
        } else if (response.data.status === "not_found") {
          setBookings([]);
        } else {
          setError(response.data.message || "Something went wrong");
        }
      } else {
        setError(response.data.message || "Failed to fetch bookings");
      }
    } catch (err) {
      console.error("Error fetching bookings:", err);
      if (err.response?.status === 404) {
        setBookings([]);
      } else if (err.response?.status === 401) {
        setError("Access token missing or invalid. Please login again.");
      } else {
        setError("Something went wrong while fetching bookings");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (token) {
      fetchBookings();
    } else {
      setError("User not logged in");
      setLoading(false);
    }
  }, [token]);

  const isTodayBetweenCheckInAndCheckOut = (checkIn, checkOut) => {
    if (!checkIn || !checkOut) return false;

    // Compare plain YYYY-MM-DD strings directly rather than round-tripping
    // through Date objects, which previously shifted checkIn/checkOut back
    // a day for viewers west of UTC (new Date("2026-07-11") parses as UTC
    // midnight, so .setHours(0,0,0,0) in a negative-offset timezone lands
    // on the 10th) while "today" itself was never shifted, desyncing the two.
    const todayStr = formatLocalDate(new Date());
    const checkInStr = String(checkIn).slice(0, 10);
    const checkOutStr = String(checkOut).slice(0, 10);

    return todayStr >= checkInStr && todayStr < checkOutStr;
  };


  // 🔄 Loading & Error States
  if (loading) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
          width: "100%",
        }}
      >
        <ClipLoader size={45} />
      </div>
    );
  }
  if (error) return <p className="error-message">{error}</p>;

  return (
    <section className="profilecontainer">
      <div className="container">
        <div className="row">
          <div className="col-12">
            <div className="profileinner">
              <div className="row">
                {/* Sidebar */}
                <Sidebar />

                {/* Main Content */}
                <div className="col-xxl-9 col-xl-9 col-lg-8 col-md-9 col-sm-12 col-12">
                  <div className="profilerightouter">
                    <div className="dltabsview">
                      {/* Tabs */}


                      {/* Tab Content */}
                      <div className="tab-content" id="myTabContent">
                        {/* Upcoming Bookings */}
                        <div
                          className="tab-pane fade show active"
                          id="Upcoming-tab-pane"
                          role="tabpanel"
                          aria-labelledby="Upcoming-tab"
                          tabIndex="0"
                        >
                          <div className="tabinner">
                            {bookings.length === 0 ? (
                              <div className="text-center py-5">
                                <h4 className="fw-bold">No Bookings Yet!</h4>
                                <p className="text-muted mb-3">
                                  You don’t have any bookings at the moment. Start exploring properties and make your first booking today.
                                </p>
                                <Link to="/" className="theme-btn">
                                  Browse Properties
                                </Link>
                              </div>
                            ) : (
                              bookings.map((booking) => (
                                <div
                                  className="triplist"
                                  key={booking.id}
                                >
                                  {/* Left Side */}
                                  <div className="triplistlt d-flex flex-column flex-lg-row gap-3 w-100">
                                    {/* Room Image */}
                                    {booking.room?.images?.length > 0 && (
                                      <div className="triplistimg">
                                        <img
                                          src={booking.room.images[0].url}
                                          alt={booking.room.title || "Room Image"}
                                        />
                                      </div>
                                    )}

                                    {/* Booking Details */}
                                    <div className="flex-fill">
                                      <h5>{booking.stay.location?.location_name || "Location not available"}</h5>
                                      <div className="date mb-1">
                                        <span>
                                          {booking.summary.check_in
                                            ? new Date(booking.summary.check_in).toLocaleDateString("en-GB", { timeZone: "UTC" })
                                            : "Check-in date not specified"}{" "}
                                          -{" "}
                                          {booking.summary.check_out
                                            ? new Date(booking.summary.check_out).toLocaleDateString("en-GB", { timeZone: "UTC" })
                                            : "Check-out date not specified"}
                                        </span>
                                        <span>{booking.summary.guest_count || 0} Guests</span>
                                      </div>
                                      <p className="mb-1 fw-semibold">{booking.room.title || "Room name not available"}</p>
                                      <p className="mb-1">{booking.stay.title || "Property name not available"}</p>
                                      <p className="mb-0 text-muted small">{booking.stay.address || "Address not provided"}</p>
                                      <p className="mb-0 text-muted small">
                                        ₹{booking.summary.base_price ?? "N/A"} / night + ₹{booking.summary.total_price - booking.summary.base_price ?? "N/A"} tax
                                      </p>
                                    </div>
                                  </div>

                                  {/* Right Side */}
                                  <div className="triplistrt text-end">
                                    {booking.summary.booking_code ? (
                                      <div className="mb-2 fw-semibold">#{booking.summary.booking_code}</div>
                                    ) : (
                                      <div className="mb-2 text-muted small">Booking code not available</div>
                                    )}
                                    <div className="mb-2">
                                      <strong>Total:</strong> ₹{booking.summary.total_price ?? "N/A"}
                                    </div>
                                    <div className="mb-2">
                                      <strong>Status: </strong>
                                      <span
                                        className={
                                          booking.summary.status === "pending"
                                            ? "text-warning"
                                            : booking.summary.status === "confirmed"
                                              ? "text-primary"
                                              : booking.summary.status === "checked_in"
                                                ? "text-warning"
                                                : booking.summary.status === "checked_out"
                                                  ? "text-success"
                                                  : booking.summary.status === "cancelled"
                                                    ? "text-danger"
                                                    : booking.summary.status === "no_show"
                                                      ? "text-danger"
                                                      : booking.summary.status === "expired"
                                                        ? "text-muted"
                                                        : ""
                                        }
                                      >
                                        {booking.summary.status === "pending" && "Pending Payment"}
                                        {booking.summary.status === "confirmed" && "Confirmed"}
                                        {booking.summary.status === "checked_in" && "Checked In"}
                                        {booking.summary.status === "checked_out" && "Completed"}
                                        {booking.summary.status === "cancelled" && "Cancelled"}
                                        {booking.summary.status === "no_show" && "No-Show"}
                                        {booking.summary.status === "expired" && "Expired"}
                                      </span>
                                    </div>


                                    {/* Owner Actions */}
                                    <div className="d-flex flex-column align-items-end gap-2">
                                      {/* {booking.summary.booking_code ? (
                                        <Link
                                          to={`/property-booking-details/${booking.summary.booking_code}`}
                                          className="theme-btn"
                                        >
                                          View Details
                                        </Link>
                                      ) : (
                                        <span className="text-muted small d-block">Details not available</span>
                                      )} */}
                                      {booking.summary.booking_code &&
                                        booking.summary.status !== "pending" && (
                                          <Link
                                            to={`/property-booking-details/${booking.summary.booking_code}`}
                                            className="theme-btn"
                                          >
                                            View Details
                                          </Link>
                                        )}

                                      {/* Action Buttons */}
                                      {booking.summary.status === "confirmed" &&
                                        isTodayBetweenCheckInAndCheckOut(
                                          booking.summary.check_in,
                                          booking.summary.check_out
                                        ) && (
                                          <button
                                            className="theme-btn mb-2"
                                            onClick={() => handleUpdateStatus(booking.id, "checkin")}
                                          >
                                            Check In
                                          </button>
                                        )}

                                      {booking.summary.status === "checked_in" && (
                                        <button
                                          className="theme-btn mb-2"
                                          onClick={() => handleUpdateStatus(booking.id, "checkout")}
                                        >
                                          Check Out
                                        </button>
                                      )}

                                      {/* Manual no-show — not date-gated, since this is for e.g. a
                                          guest calling ahead to say they aren't coming, ahead of the
                                          automatic cutoff-driven cron. */}
                                      {booking.summary.status === "confirmed" && (
                                        <button
                                          className="theme-btn mb-2 btn-outline-danger"
                                          onClick={() => {
                                            if (window.confirm("Mark this booking as a no-show? This cannot be undone and the stay will not be refunded.")) {
                                              handleUpdateStatus(booking.id, "no_show");
                                            }
                                          }}
                                        >
                                          Mark No-Show
                                        </button>
                                      )}
                                    </div>


                                  </div>
                                </div>
                              ))
                            )}
                          </div>


                        </div>

                        {/* Cancelled Bookings */}
                        <div
                          className="tab-pane fade"
                          id="Cancelled-tab-pane"
                          role="tabpanel"
                          aria-labelledby="Cancelled-tab"
                          tabIndex="0"
                        >
                          {/* Filter bookings with status 'cancelled' */}
                          <div className="tabinner">
                            {bookings.filter(b => b.summary.status === "cancelled").length === 0 ? (
                              <p className="text-center py-3">No cancelled bookings.</p>
                            ) : (
                              bookings
                                .filter(b => b.summary.status === "cancelled")
                                .map((booking) => (
                                  <div className="triplist d-flex justify-content-between align-items-start mb-3 p-3 bg-white shadow-sm rounded" key={booking.id}>
                                    <div className="triplistlt">
                                      <h5>{booking.stay.location?.location_name || "--"}</h5>
                                      <div className="date mb-1">
                                        <span>
                                          {booking.summary.check_in
                                            ? new Date(booking.summary.check_in).toLocaleDateString("en-GB", { timeZone: "UTC" })
                                            : "--"}{" "}
                                          -{" "}
                                          {booking.summary.check_out
                                            ? new Date(booking.summary.check_out).toLocaleDateString("en-GB", { timeZone: "UTC" })
                                            : "--"}
                                        </span>
                                        <span>{booking.summary.guest_count || 0} People</span>
                                      </div>
                                      <p className="mb-1">{booking.stay.title || "--"}</p>
                                      <p className="mb-0">{booking.stay.address || "--"}</p>
                                    </div>
                                    <div className="triplistrt">
                                      {booking.summary.booking_code && (
                                        <Link
                                          to={`/booking-details/${booking.summary.booking_code}`}
                                          className="theme-btn"
                                        >
                                          View Details
                                        </Link>
                                      )}
                                    </div>
                                  </div>
                                ))
                            )}
                          </div>
                        </div>

                        {/* Completed (Checked-Out) Bookings */}
                        <div
                          className="tab-pane fade"
                          id="Completed-tab-pane"
                          role="tabpanel"
                          aria-labelledby="Completed-tab"
                          tabIndex="0"
                        >
                          <div className="tabinner">
                            {bookings.filter(b => b.summary.status === "checked_out").length === 0 ? (
                              <p className="text-center py-3">No completed bookings.</p>
                            ) : (
                              bookings
                                .filter(b => b.summary.status === "checked_out")
                                .map((booking) => (
                                  <div className="triplist d-flex justify-content-between align-items-start mb-3 p-3 bg-white shadow-sm rounded" key={booking.id}>
                                    <div className="triplistlt">
                                      <h5>{booking.stay.location?.location_name || "--"}</h5>
                                      <div className="date mb-1">
                                        <span>
                                          {booking.summary.check_in
                                            ? new Date(booking.summary.check_in).toLocaleDateString("en-GB", { timeZone: "UTC" })
                                            : "--"}{" "}
                                          -{" "}
                                          {booking.summary.check_out
                                            ? new Date(booking.summary.check_out).toLocaleDateString("en-GB", { timeZone: "UTC" })
                                            : "--"}
                                        </span>
                                        <span>{booking.summary.guest_count || 0} People</span>
                                      </div>
                                      <p className="mb-1">{booking.stay.title || "--"}</p>
                                      <p className="mb-0">{booking.stay.address || "--"}</p>
                                    </div>
                                    <div className="triplistrt">
                                      {booking.summary.booking_code && (
                                        <Link
                                          to={`/booking-details/${booking.summary.booking_code}`}
                                          className="theme-btn"
                                        >
                                          View Details
                                        </Link>
                                      )}
                                    </div>
                                  </div>
                                ))
                            )}
                          </div>
                        </div>

                        {/* No-Show Bookings */}
                        <div
                          className="tab-pane fade"
                          id="NoShow-tab-pane"
                          role="tabpanel"
                          aria-labelledby="NoShow-tab"
                          tabIndex="0"
                        >
                          <div className="tabinner">
                            {bookings.filter(b => b.summary.status === "no_show").length === 0 ? (
                              <p className="text-center py-3">No no-show bookings.</p>
                            ) : (
                              bookings
                                .filter(b => b.summary.status === "no_show")
                                .map((booking) => (
                                  <div className="triplist d-flex justify-content-between align-items-start mb-3 p-3 bg-white shadow-sm rounded" key={booking.id}>
                                    <div className="triplistlt">
                                      <h5>{booking.stay.location?.location_name || "--"}</h5>
                                      <div className="date mb-1">
                                        <span>
                                          {booking.summary.check_in
                                            ? new Date(booking.summary.check_in).toLocaleDateString("en-GB", { timeZone: "UTC" })
                                            : "--"}{" "}
                                          -{" "}
                                          {booking.summary.check_out
                                            ? new Date(booking.summary.check_out).toLocaleDateString("en-GB", { timeZone: "UTC" })
                                            : "--"}
                                        </span>
                                        <span>{booking.summary.guest_count || 0} People</span>
                                      </div>
                                      <p className="mb-1">{booking.stay.title || "--"}</p>
                                      <p className="mb-0">{booking.stay.address || "--"}</p>
                                    </div>
                                    <div className="triplistrt">
                                      {booking.summary.booking_code && (
                                        <Link
                                          to={`/booking-details/${booking.summary.booking_code}`}
                                          className="theme-btn"
                                        >
                                          View Details
                                        </Link>
                                      )}
                                    </div>
                                  </div>
                                ))
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default PropertyBookings;
